import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  Search, 
  RefreshCw,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  FileText,
  Clock,
  User,
  Shield,
  Activity,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  auth: 'bg-blue-100 text-blue-800',
  user: 'bg-green-100 text-green-800',
  order: 'bg-purple-100 text-purple-800',
  wallet: 'bg-yellow-100 text-yellow-800',
  product: 'bg-pink-100 text-pink-800',
  quotation: 'bg-orange-100 text-orange-800',
  system: 'bg-slate-100 text-slate-800'
};

const ACTION_ICONS = {
  login: <User className="h-4 w-4 text-green-600" />,
  login_failed: <AlertTriangle className="h-4 w-4 text-red-600" />,
  logout: <User className="h-4 w-4 text-slate-600" />,
  create: <FileText className="h-4 w-4 text-blue-600" />,
  update: <Activity className="h-4 w-4 text-yellow-600" />,
  delete: <Trash2 className="h-4 w-4 text-red-600" />
};

export default function AdminActivityLogs() {
  const { canDelete, hasPermission } = useAuth();
  const canDeleteActivityLogs = hasPermission('activity_logs:delete');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('all');
  const [action, setAction] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [page, startDate, endDate, category, action]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (category !== 'all') params.append('category', category);
      if (action !== 'all') params.append('action', action);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/admin/activity-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setCategories(response.data.categories || []);
      setActions(response.data.actions || []);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setCategory('all');
    setAction('all');
    setPage(1);
  };

  const viewLogDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLogs.length === logs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(logs.map(log => log.id));
    }
  };

  const deleteLog = async (logId) => {
    if (!canDeleteActivityLogs) {
      toast.error('Permission denied: activity_logs:delete required');
      return;
    }
    try {
      await axios.delete(`${API}/admin/activity-logs/${logId}`);
      toast.success('Log deleted successfully');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete log');
    }
  };

  const bulkDeleteLogs = async () => {
    if (!canDeleteActivityLogs) {
      toast.error('Permission denied: activity_logs:delete required');
      return;
    }
    try {
      await axios.post(`${API}/admin/activity-logs/bulk-delete`, selectedLogs);
      toast.success(`Deleted ${selectedLogs.length} logs`);
      setSelectedLogs([]);
      setDeleteConfirmOpen(false);
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logs');
    }
  };

  const clearAllLogs = async () => {
    if (!canDeleteActivityLogs) {
      toast.error('Permission denied: activity_logs:delete required');
      return;
    }
    try {
      await axios.delete(`${API}/admin/activity-logs/clear-all`);
      toast.success('All logs cleared');
      setClearAllConfirmOpen(false);
      fetchLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    if (actionType?.includes('login')) return ACTION_ICONS[actionType] || ACTION_ICONS.login;
    if (actionType?.includes('create')) return ACTION_ICONS.create;
    if (actionType?.includes('update')) return ACTION_ICONS.update;
    if (actionType?.includes('delete')) return ACTION_ICONS.delete;
    return <Activity className="h-4 w-4 text-slate-600" />;
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap md-nowrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" data-testid="logs-title">
              Activity Logs
            </h1>
            <p className="text-slate-500 mt-1">Complete audit trail of all system activities</p>
          </div>
          <div className="flex gap-2 mt-2 md:m-0">
            <Button onClick={fetchLogs} variant="outline" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {canDeleteActivityLogs && (
              <Button 
                variant="destructive" 
                onClick={() => setClearAllConfirmOpen(true)}
                data-testid="clear-all-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Summary Card */}
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Log Entries</p>
                  <p className="text-2xl font-bold text-slate-800">{total}</p>
                </div>
              </div>
              {canDeleteActivityLogs && selectedLogs.length > 0 && (
                <Button 
                  variant="destructive" 
                  onClick={() => setDeleteConfirmOpen(true)}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedLogs.length})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm text-slate-600 mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search logs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
              </div>
              {/* Date Range */}
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="flex-1 sm:flex-initial">
                  <Label className="text-sm text-slate-600 mb-1 block">From</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                    className="w-full sm:w-40 min-w-[135px] px-2 text-xs sm:text-sm"
                    data-testid="start-date"
                  />
                </div>
                <div className="flex-1 sm:flex-initial">
                  <Label className="text-sm text-slate-600 mb-1 block">To</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                    className="w-full sm:w-40 min-w-[135px] px-2 text-xs sm:text-sm"
                    data-testid="end-date"
                  />
                </div>
              </div>
              {/* Category */}
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Category</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
                  <SelectTrigger className="w-32" data-testid="category-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Action */}
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Action</Label>
                <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
                  <SelectTrigger className="w-36" data-testid="action-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {actions.map(act => (
                      <SelectItem key={act} value={act}>{act.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Buttons */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="bg-white border-0 shadow-sm" data-testid="logs-table">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-16">
                <Shield className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No activity logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <Checkbox
                          checked={selectedLogs.length === logs.length && logs.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Time</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Action</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Description</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50 transition-colors" data-testid={`log-row-${log.id}`}>
                        <td className="px-4 py-3 text-nowrap">
                          <Checkbox
                            checked={selectedLogs.includes(log.id)}
                            onCheckedChange={() => handleSelectLog(log.id)}
                          />
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <div className="flex items-center gap-2 text-sm text-slate-600  text-nowrap">
                            <Clock className="h-4 w-4" />
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className="text-sm font-medium capitalize">
                              {log.action?.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <Badge className={CATEGORY_COLORS[log.category] || 'bg-slate-100 text-slate-800'}>
                            {log.category}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <div>
                            <p className="text-sm font-medium">{log.user_email || 'System'}</p>
                            {log.user_role && (
                              <p className="text-xs text-slate-500 capitalize">{log.user_role.replace('_', ' ')}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <p className="text-sm text-slate-600 truncate max-w-xs" title={log.description}>
                            {log.description}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-center text-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewLogDetails(log)}
                              data-testid={`view-log-${log.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canDeleteActivityLogs && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => deleteLog(log.id)}
                                data-testid={`delete-log-${log.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, total)} of {total} logs
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Log Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Log Details
              </DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Log ID</p>
                    <p className="font-mono text-sm">{selectedLog.id}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Date & Time</p>
                    <p className="text-sm">{formatDate(selectedLog.created_at)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Action</p>
                    <div className="flex items-center gap-2">
                      {getActionIcon(selectedLog.action)}
                      <span className="capitalize font-medium">{selectedLog.action?.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Category</p>
                    <Badge className={CATEGORY_COLORS[selectedLog.category] || 'bg-slate-100 text-slate-800'}>
                      {selectedLog.category}
                    </Badge>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">User Information</p>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm">{selectedLog.user_email || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Role</p>
                      <p className="text-sm capitalize">{selectedLog.user_role?.replace('_', ' ') || 'N/A'}</p>
                    </div>
                    {selectedLog.user_id && (
                      <div className="space-y-1 col-span-2">
                        <p className="text-xs text-slate-500">User ID</p>
                        <p className="font-mono text-xs">{selectedLog.user_id}</p>
                      </div>
                    )}
                  </div>
                </div>

                {(selectedLog.target_id || selectedLog.target_type) && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Target</p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-lg">
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Type</p>
                        <p className="text-sm capitalize">{selectedLog.target_type || 'N/A'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">ID</p>
                        <p className="font-mono text-xs">{selectedLog.target_id || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-xs text-slate-500 mb-1">Description</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedLog.description}</p>
                </div>

                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div className="border-t pt-4">
                    <p className="text-xs text-slate-500 mb-1">Additional Data</p>
                    <pre className="text-xs bg-slate-50 p-3 rounded-lg overflow-auto">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Selected Logs</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {selectedLogs.length} selected log entries? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={bulkDeleteLogs}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clear All Confirmation */}
        <Dialog open={clearAllConfirmOpen} onOpenChange={setClearAllConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clear All Logs</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete ALL activity logs? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClearAllConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={clearAllLogs}>Clear All</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
